import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import useAudioPlayer from '../../hooks/useAudioPlayer';
import { useFreePlay } from '../../context/FreePlayContext';
import { useAuth } from '../../context/AuthContext';
import { startGame as apiStartGame, gameSpin as apiGameSpin } from '../../api/backend';
import {
  GUEST_CONFIG,
  clearGuestGame,
  isTrialUsed,
  loadGuestGame,
  markTrialDone,
  newGuestGame,
  randomSegmentNumber,
  resolveGuestSpin,
  saveGuestGame
} from '../../game/guestEngine';
import MusicPlayer from './MusicPlayer';
import Scoreboard from './Scoreboard';
import InstructionsScreen from './InstructionsScreen';
import AdBanner from '../Ads/AdBanner';
import './MinimalWheel.css';

const DEFAULT_SEGMENT_COUNT = 42;

const getSegmentColor = (index) => {
  const segment = index + 1;
  if ([41, 42, 1].includes(segment)) return '#81C784';
  if ([20, 21, 22].includes(segment)) return '#81C784';
  if ([9, 10, 11, 12].includes(segment)) return '#FFEB3B';
  if ([30, 31, 32, 33].includes(segment)) return '#FFEB3B';
  if ((segment >= 32 && segment <= 42) || (segment >= 1 && segment <= 10)) {
    return '#3B82F6';
  }
  return '#F7931A';
};

const getSegmentType = (index) => {
  const segment = index + 1;
  if ([42, 1, 2, 21, 22, 23].includes(segment)) return 'superWin';
  if ([10, 11, 12, 13, 31, 32, 33, 34].includes(segment)) return 'tie';
  return 'regular';
};

const createDefaultSegments = (length = DEFAULT_SEGMENT_COUNT) => (
  Array.from({ length }, (_, i) => ({
    angle: (i * 360) / length,
    color: getSegmentColor(i),
    type: getSegmentType(i),
    index: i
  }))
);

// Rest the pointer centered on the middle green segment (segment 42, index 41)
// of the top green trio, rather than on the line between two segments. This
// reuses the same centering math as a landing (see targetAngle in handleSpin)
// so the starting position looks just like a clean landing on that segment.
const START_SEGMENT_INDEX = 41;
const START_ANGLE =
  360 - (START_SEGMENT_INDEX * 360) / DEFAULT_SEGMENT_COUNT - 180 / DEFAULT_SEGMENT_COUNT;

// Wheel-position convention: top-middle segment is game #1 and numbers
// increase clockwise, so wheel index = ((number - 2) + 42) % 42 and
// number = ((index + 1) % 42) + 1.
const wheelIndexForNumber = (number) => ((number - 2) + 42) % 42;
// Inverse of wheelIndexForNumber: the segment number painted at wheel index i
const numberForWheelIndex = (i) => ((i + 1) % 42) + 1;

// UI letter -> engine color name
const COLOR_MAP = { B: 'blue', G: 'green', Y: 'yellow', O: 'orange' };

const newClientSpinId = () =>
  (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `spin-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const MinimalWheel = () => {
  // Welcome/instructions overlay: show once per browser session, not on every
  // remount (navigating to Instructions/Dashboard and back re-creates this
  // component and would otherwise re-show the intro).
  const [showInstructions, setShowInstructions] = useState(() => {
    try {
      return window.sessionStorage.getItem('bitwheel.introSeen') !== '1';
    } catch (e) {
      return true;
    }
  });
  const [isSpinning, setIsSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [resultDetail, setResultDetail] = useState(null);
  const [landedSegment, setLandedSegment] = useState(null);
  // Every winning number this game, in spin order, for the history row
  const [numberHistory, setNumberHistory] = useState([]);
  // Press-and-hold on the wheel reveals each segment's number
  const [showNumbers, setShowNumbers] = useState(false);
  const controls = useAnimation();
  const [segments] = useState(() => createDefaultSegments());
  const [game, setGame] = useState(null); // server game session state
  // One-time guest trial: resume an in-progress trial from this tab's session,
  // or grant a fresh 10-spin game if this browser hasn't used its trial yet.
  const [guestGame, setGuestGame] = useState(() => loadGuestGame() || (isTrialUsed() ? null : newGuestGame()));
  const [endGame, setEndGame] = useState(null); // end-of-game summary
  const [statusMessage, setStatusMessage] = useState('');
  const [selectedWager, setSelectedWager] = useState(1);
  const [selectedColor, setSelectedColor] = useState('B');
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [lockSelections, setLockSelections] = useState(false);
  const startingRef = useRef(false);
  const { openPayment } = useFreePlay();
  const {
    isAuthenticated,
    loading: authLoading,
    account,
    token,
    refreshAccount,
    openAuth
  } = useAuth();

  const scoreboardName = isAuthenticated && account?.email
    ? `P1 ${account.gamerTag || account.email}`
    : 'P1 Guest';
  const { winnerSoundRef, wheelButtonSoundRef } = useAudioPlayer();

  // Slightly larger board on desktop; phones/tablets keep the compact wheel.
  const COIN_SIZE =
    typeof window !== 'undefined' && window.innerWidth >= 769 ? 360 : 320;

  // ----- Logged-in: real game sessions on the shared backend -----
  const beginGame = useCallback(async () => {
    if (startingRef.current || !token) {
      return;
    }
    startingRef.current = true;
    try {
      const data = await apiStartGame(token);
      setGame(data);
      setEndGame(null);
      setNumberHistory([]);
      setStatusMessage(data.resumed ? 'Game resumed — good luck!' : '');
      if (!data.resumed) {
        refreshAccount();
      }
    } catch (error) {
      if (error.status === 403) {
        setStatusMessage('Out of spins');
        openPayment('limit');
      } else if (error.status === 401) {
        setStatusMessage('Please log in to play');
      } else {
        console.error(error);
        setStatusMessage('Unable to start game');
      }
    } finally {
      startingRef.current = false;
    }
  }, [token, refreshAccount, openPayment]);

  useEffect(() => {
    if (authLoading || showInstructions) {
      return;
    }
    if (isAuthenticated) {
      if (!game) {
        beginGame();
      }
    } else if (guestGame) {
      setStatusMessage('Free trial game — 10 spins on the house!');
    } else {
      setStatusMessage('Sign up free to play — get 20 free spins!');
    }
  }, [authLoading, showInstructions, isAuthenticated, game, guestGame, beginGame]);

  // Never leave an unaffordable wager selected (0 is always allowed).
  const spinPointsLeft = isAuthenticated
    ? (game?.spinPointsRemaining ?? 10)
    : (guestGame?.spinPointsRemaining ?? 0);
  useEffect(() => {
    if ((game || guestGame) && selectedWager > spinPointsLeft) {
      setSelectedWager(spinPointsLeft >= 1 ? 1 : 0);
    }
  }, [game, guestGame, selectedWager, spinPointsLeft]);

  const animateToSegment = useCallback(async (wheelIndex) => {
    const fullRotations = 10;
    const spinDuration = 10; // seconds
    const segmentAngle = 360 / segments.length;
    // Add half segment angle to center the pointer in the middle of the segment
    const targetAngle = 360 - (wheelIndex * segmentAngle) - (segmentAngle / 2);
    // Negative total spins the wheel counterclockwise; it still settles on the
    // same final orientation (targetAngle mod 360), so the pointer lands on the
    // correct segment.
    const totalRotation = targetAngle - fullRotations * 360;

    // Lock selections 1 second before spin ends
    setTimeout(() => {
      setLockSelections(true);
    }, (spinDuration - 1) * 1000);

    await controls.start({
      rotateZ: totalRotation,
      // Real-wheel physics feel: launch at ~5x average speed, then
      // decelerate continuously to a crawl (easeOutQuart curve) — most of
      // the rotation happens in the first seconds, and the final segments
      // tick past slowly enough to build suspense.
      transition: { duration: spinDuration, ease: [0.165, 0.84, 0.44, 1] }
    });

    setTimeout(() => {
      controls.set({ rotateZ: targetAngle });
    }, 100);

    return targetAngle;
  }, [controls, segments.length]);

  const playSpinSound = () => {
    if (wheelButtonSoundRef.current) {
      wheelButtonSoundRef.current.currentTime = 0;
      wheelButtonSoundRef.current.play().catch(() => {});
    }
  };

  const playWinSound = () => {
    // Stop the spin music first so the winning jingle plays clean after it.
    if (wheelButtonSoundRef.current) {
      wheelButtonSoundRef.current.pause();
    }
    if (winnerSoundRef.current) {
      winnerSoundRef.current.currentTime = 0;
      winnerSoundRef.current.play().catch(() => {});
    }
  };

  // Winning-number flash: the landed number blinks twice over the wheel in
  // big retro white type when the spin settles.
  const [winFlash, setWinFlash] = useState(null);
  const winFlashTimerRef = useRef(null);
  // The wheel area, so pressing SPIN can scroll the whole board into view
  const wheelAreaRef = useRef(null);

  const triggerWinFlash = (number) => {
    if (winFlashTimerRef.current) clearTimeout(winFlashTimerRef.current);
    setWinFlash(null);
    // Re-mount on the next frame so the CSS animation restarts every spin.
    requestAnimationFrame(() => {
      setWinFlash(number);
      winFlashTimerRef.current = setTimeout(() => setWinFlash(null), 1700);
    });
  };

  // Auto-hide the result pill and its detail line a few seconds after each
  // spin resolves, so stale results don't linger between spins.
  useEffect(() => {
    if (!result) return undefined;
    const timer = setTimeout(() => {
      setResult(null);
      setResultDetail(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [result]);

  // Retro blips for the wager/color pickers: each option gets its own note
  // (wagers climb in pitch with the stakes; colors each have a distinct
  // two-tone), synthesized with the Web Audio API — no audio files.
  const optionAudioCtxRef = useRef(null);

  const playOptionBlip = (freqs) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!optionAudioCtxRef.current) optionAudioCtxRef.current = new Ctx();
      const ctx = optionAudioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      (Array.isArray(freqs) ? freqs : [freqs]).forEach((freq, i) => {
        const start = ctx.currentTime + i * 0.075;
        const dur = 0.08;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.14, start + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + dur);
      });
    } catch (e) {
      // Sound is best-effort; never block a selection on audio errors.
    }
  };

  const handleSpin = async () => {
    if (isSpinning) return;
    setShowNumbers(false);

    // The spin button sits below the wheel, so on small screens the top of
    // the wheel is often scrolled out of view when pressed. Glide the whole
    // board back into view ('nearest' scrolls the minimum needed and does
    // nothing when it's already fully visible).
    wheelAreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    if (isAuthenticated) {
      if (!game || game.status !== 'active') {
        if (game?.status === 'complete') {
          setEndGame((prev) => prev || { finalScore: game.finalScore });
        } else {
          beginGame();
        }
        return;
      }

      try {
        setIsSpinning(true);
        setResult(null);
        setResultDetail(null);
        setLandedSegment(null);
        setStatusMessage('Spinning...');
        playSpinSound();

        // Server decides the outcome; the client only animates to it.
        const data = await apiGameSpin({
          sessionId: game.sessionId,
          wager: selectedWager,
          chosenColor: COLOR_MAP[selectedColor] || null,
          chosenNumber: selectedNumber ? Number(selectedNumber) : null,
          clientSpinId: newClientSpinId()
        }, token);

        const spin = data.spin;
        const wheelIndex = wheelIndexForNumber(spin.resultSegment);
        await animateToSegment(wheelIndex);

        setResult(
          spin.payout > 0
            ? `+${spin.payout.toLocaleString()}`
            : spin.matchType === 'skip' ? 'OBSERVED' : 'NO MATCH'
        );
        setResultDetail(data.message || null);
        setLandedSegment({
          number: spin.resultSegment,
          color: segments[wheelIndex]?.color || '#000000'
        });
        setNumberHistory((prev) => [
          ...prev,
          { number: spin.resultSegment, color: segments[wheelIndex]?.color || '#ffffff' }
        ]);
        triggerWinFlash(spin.resultSegment);
        setStatusMessage('');
        setGame((prev) => ({
          ...prev,
          score: data.score,
          spinPointsRemaining: data.spinPointsRemaining,
          spinsRemaining: data.spinsRemaining,
          status: data.status,
          finalScore: data.finalScore
        }));
        if (spin.payout > 0) {
          playWinSound();
        }

        setTimeout(() => {
          setIsSpinning(false);
          setLockSelections(false);
          if (data.status === 'complete') {
            setEndGame({
              finalScore: data.finalScore,
              bestSpin: data.stats?.bestSpinThisGame ?? 0,
              highScore: data.stats?.highScore ?? data.finalScore,
              newHighScore: Boolean(data.stats?.newHighScore)
            });
            refreshAccount();
          }
        }, 2500);
      } catch (error) {
        console.error(error);
        setStatusMessage(error.message || 'Spin failed');
        setIsSpinning(false);
      }
      return;
    }

    // ----- Guest trial spin (full rules, resolved locally, session-only) -----
    if (!guestGame || guestGame.status !== 'active' || guestGame.spinsRemaining <= 0) {
      // Trial exhausted (or storage-less browser): funnel to the 20-spin signup.
      openAuth('signup20');
      return;
    }

    const wager = Math.min(selectedWager, guestGame.spinPointsRemaining);
    try {
      setIsSpinning(true);
      setResult(null);
      setResultDetail(null);
      setLandedSegment(null);
      setStatusMessage('Spinning...');
      playSpinSound();

      const resultNumber = randomSegmentNumber();
      const spin = resolveGuestSpin({
        resultNumber,
        wager,
        chosenColor: COLOR_MAP[selectedColor] || null,
        chosenNumber: selectedNumber ? Number(selectedNumber) : null
      });

      const nextGame = {
        ...guestGame,
        score: guestGame.score + spin.payout,
        spinPointsRemaining: guestGame.spinPointsRemaining - wager,
        spinsRemaining: guestGame.spinsRemaining - 1,
        bestSpin: Math.max(guestGame.bestSpin, spin.payout)
      };
      // Complete when spins run out OR wager points are spent — 0 points
      // left means every remaining spin would be a scoreless observe spin.
      if (nextGame.spinsRemaining <= 0 || nextGame.spinPointsRemaining <= 0) {
        nextGame.status = 'complete';
      }

      // Persist the outcome IMMEDIATELY, before the long wheel animation —
      // if the tab reloads or is discarded mid-spin (common on mobile), the
      // game resumes with the correct state instead of stranding the player.
      if (nextGame.status === 'complete') {
        markTrialDone();
        clearGuestGame();
      } else {
        saveGuestGame(nextGame);
      }

      const wheelIndex = wheelIndexForNumber(resultNumber);
      await animateToSegment(wheelIndex);

      setResult(
        spin.payout > 0
          ? `+${spin.payout.toLocaleString()}`
          : spin.matchType === 'skip' ? 'OBSERVED' : 'NO MATCH'
      );
      setResultDetail(spin.message);
      setLandedSegment({
        number: resultNumber,
        color: segments[wheelIndex]?.color || '#000000'
      });
      setNumberHistory((prev) => [
        ...prev,
        { number: resultNumber, color: segments[wheelIndex]?.color || '#ffffff' }
      ]);
      triggerWinFlash(resultNumber);
      setStatusMessage('');
      setGuestGame(nextGame);
      if (spin.payout > 0) {
        playWinSound();
      }

      setTimeout(() => {
        setIsSpinning(false);
        setLockSelections(false);
        if (nextGame.status === 'complete') {
          setEndGame({
            finalScore: nextGame.score,
            bestSpin: nextGame.bestSpin,
            guest: true
          });
        }
      }, 2500);
    } catch (error) {
      console.error(error);
      setStatusMessage('Spin failed');
      setIsSpinning(false);
    }
  };

  const handlePlayAgain = () => {
    setEndGame(null);
    setGame(null);
    setResult(null);
    setResultDetail(null);
    setLandedSegment(null);
    setNumberHistory([]);
    beginGame();
  };

  const coinStyle = useMemo(() => ({
    width: `${COIN_SIZE}px`,
    height: `${COIN_SIZE}px`,
    borderRadius: '50%',
    position: 'relative',
    transformStyle: 'preserve-3d',
    background: '#1a237e',
    boxShadow: '0 0 40px rgba(14, 165, 233, 0.5), inset 0 0 20px rgba(0,0,0,0.3)'
  }), [COIN_SIZE]);

  const radius = COIN_SIZE / 2;

  const handleStartGame = () => {
    try {
      window.sessionStorage.setItem('bitwheel.introSeen', '1');
    } catch (e) {
      // Storage unavailable (private mode) — the overlay still dismisses.
    }
    setShowInstructions(false);
  };

  const activeAnyGame = isAuthenticated ? game?.status === 'active' : guestGame?.status === 'active';
  const observingOnly = activeAnyGame && spinPointsLeft <= 0;
  const gameForBoard = isAuthenticated && game
    ? {
        score: game.score,
        spinPoints: game.spinPointsRemaining,
        spinPointsTotal: game.startingSpinPoints ?? 10,
        spinsLeft: game.spinsRemaining,
        spinsTotal: game.spinsPerGame ?? 10,
        highScore: account?.highScore ?? 0
      }
    : !isAuthenticated && guestGame
      ? {
          score: guestGame.score,
          spinPoints: guestGame.spinPointsRemaining,
          spinPointsTotal: GUEST_CONFIG.startingSpinPoints,
          spinsLeft: guestGame.spinsRemaining,
          spinsTotal: GUEST_CONFIG.spinsPerGame,
          highScore: 0
        }
      : null;

  return (
    <div className="minimal-wheel">
      {/* Instructions Screen */}
      {showInstructions && <InstructionsScreen onStart={handleStartGame} />}

      {/* Status scoreboard (grey retro bar) above the music controls */}
      <Scoreboard
        playerName={scoreboardName}
        game={gameForBoard}
        lastNumber={landedSegment?.number ?? null}
        lastColor={landedSegment?.color}
      />

      {/* Music Player */}
      <MusicPlayer />

      {/* Segment Count Display */}
      <div className="minimal-wheel__segment-count">42</div>

      <div className="minimal-wheel__container">
        {/* Wheel */}
        <div
          className="minimal-wheel__wheel-area"
          ref={wheelAreaRef}
          onPointerDown={() => { if (!isSpinning) setShowNumbers(true); }}
          onPointerUp={() => setShowNumbers(false)}
          onPointerLeave={() => setShowNumbers(false)}
          onPointerCancel={() => setShowNumbers(false)}
          onContextMenu={(e) => e.preventDefault()}
        >
          <motion.div style={coinStyle} animate={controls} initial={{ rotateZ: START_ANGLE }}>
            <svg width={COIN_SIZE} height={COIN_SIZE} viewBox={`0 0 ${COIN_SIZE} ${COIN_SIZE}`}>
              {segments.map((seg, i) => {
                const startAngle = (seg.angle - 90) * (Math.PI / 180);
                const endAngle = ((seg.angle + 360 / segments.length - 90) * Math.PI / 180);
                const x1 = radius + radius * Math.cos(startAngle);
                const y1 = radius + radius * Math.sin(startAngle);
                const x2 = radius + radius * Math.cos(endAngle);
                const y2 = radius + radius * Math.sin(endAngle);
                const largeArc = 360 / segments.length > 180 ? 1 : 0;

                return (
                  <path
                    key={i}
                    d={`M ${radius},${radius} L ${x1},${y1} A ${radius},${radius} 0 ${largeArc},1 ${x2},${y2} Z`}
                    fill={seg.color}
                    stroke="#1a1a1a"
                    strokeWidth="1"
                  />
                );
              })}
              <circle cx={radius} cy={radius} r={radius * 0.15} fill="#1a237e" />
              {/* Hold-to-peek: every segment's number at its widest (outer)
                  band, thin white type, radial like a classic prize wheel.
                  Rendered inside the rotating svg so labels stay glued to
                  their segments at any wheel rotation. */}
              {showNumbers && segments.map((seg, i) => {
                const step = 360 / segments.length;
                const midDeg = seg.angle + step / 2 - 90;
                const midRad = (midDeg * Math.PI) / 180;
                const rr = radius * 0.90;
                const x = radius + rr * Math.cos(midRad);
                const y = radius + rr * Math.sin(midRad);
                // Radial orientation; flip numbers on the left half upright
                const norm = ((midDeg % 360) + 360) % 360;
                const rot = norm > 90 && norm < 270 ? midDeg + 180 : midDeg;
                return (
                  <text
                    key={`num-${i}`}
                    x={x}
                    y={y}
                    fill={seg.color === '#FFEB3B' ? '#101010' : '#FFFFFF'}
                    fontSize="13"
                    fontFamily="'Segoe UI', 'Helvetica Neue', Arial, sans-serif"
                    fontWeight="300"
                    textAnchor="middle"
                    dominantBaseline="central"
                    transform={`rotate(${rot}, ${x}, ${y})`}
                    style={{ pointerEvents: 'none' }}
                  >
                    {numberForWheelIndex(i)}
                  </text>
                );
              })}
            </svg>
          </motion.div>

          {/* Pointer */}
          <div className="minimal-wheel__pointer">▼</div>

          {/* Winning number on the center hub: pops up AFTER the big flash
              over the board finishes (cleared when the next spin starts) */}
          {landedSegment != null && winFlash == null && (
            <div className="minimal-wheel__hub-number">{landedSegment.number}</div>
          )}

          {/* Winning number: flashes twice over the wheel when it stops */}
          {winFlash != null && (
            <div className="minimal-wheel__win-flash" key={winFlash}>
              {winFlash}
            </div>
          )}
        </div>

        {/* Status Message */}
        {statusMessage && (
          <div className="minimal-wheel__status">{statusMessage}</div>
        )}
        {observingOnly && !statusMessage && (
          <div className="minimal-wheel__status">
            Observing only — spin points spent. Watch and learn the wheel!
          </div>
        )}

        {/* Betting Controls */}
        <div className="minimal-wheel__betting">
          <div className="minimal-wheel__bet-line">
            <span className="minimal-wheel__bet-label">WAGER:</span>
            <div className="minimal-wheel__bet-options">
              <button
                className={`minimal-wheel__wager-option minimal-wheel__wager-option--zero ${selectedWager === 0 ? 'selected' : ''}`}
                onClick={() => { if (!lockSelections) { playOptionBlip(262); setSelectedWager(0); } }}
                disabled={lockSelections}
                style={{ color: '#000000' }}
              >
                0
              </button>
              <span className="minimal-wheel__divider">/</span>
              <button
                className={`minimal-wheel__wager-option ${selectedWager === 1 ? 'selected' : ''}`}
                onClick={() => { if (!lockSelections && !(isAuthenticated && game && spinPointsLeft < 1)) { playOptionBlip(392); setSelectedWager(1); } }}
                disabled={lockSelections || (isAuthenticated && game && spinPointsLeft < 1)}
                style={{ color: '#ef4444' }}
              >
                1
              </button>
              <span className="minimal-wheel__divider">/</span>
              <button
                className={`minimal-wheel__wager-option ${selectedWager === 2 ? 'selected' : ''}`}
                onClick={() => { if (!lockSelections && !(isAuthenticated && game && spinPointsLeft < 2)) { playOptionBlip(523); setSelectedWager(2); } }}
                disabled={lockSelections || (isAuthenticated && game && spinPointsLeft < 2)}
                style={{ color: '#22c55e' }}
              >
                2
              </button>
            </div>
          </div>

          <div className="minimal-wheel__bet-line">
            <span className="minimal-wheel__bet-label">COLOR:</span>
            <div className="minimal-wheel__bet-options">
              <button
                className={`minimal-wheel__color-option ${selectedColor === 'B' ? 'selected' : ''}`}
                onClick={() => { if (!lockSelections) { playOptionBlip([494, 659]); setSelectedColor('B'); } }}
                disabled={lockSelections}
                style={{ color: '#3B82F6' }}
              >
                B
              </button>
              <span className="minimal-wheel__divider">/</span>
              <button
                className={`minimal-wheel__color-option ${selectedColor === 'G' ? 'selected' : ''}`}
                onClick={() => { if (!lockSelections) { playOptionBlip([587, 784]); setSelectedColor('G'); } }}
                disabled={lockSelections}
                style={{ color: '#22c55e' }}
              >
                G
              </button>
              <span className="minimal-wheel__divider">/</span>
              <button
                className={`minimal-wheel__color-option ${selectedColor === 'Y' ? 'selected' : ''}`}
                onClick={() => { if (!lockSelections) { playOptionBlip([659, 880]); setSelectedColor('Y'); } }}
                disabled={lockSelections}
                style={{ color: '#FFEB3B' }}
              >
                Y
              </button>
              <span className="minimal-wheel__divider">/</span>
              <button
                className={`minimal-wheel__color-option ${selectedColor === 'O' ? 'selected' : ''}`}
                onClick={() => { if (!lockSelections) { playOptionBlip([740, 988]); setSelectedColor('O'); } }}
                disabled={lockSelections}
                style={{ color: '#F7931A' }}
              >
                O
              </button>
            </div>
          </div>

          <div className="minimal-wheel__bet-line">
            <span className="minimal-wheel__bet-label">#:</span>
            <input
              type="number"
              className="minimal-wheel__number-input"
              value={selectedNumber || ''}
              onChange={(e) => !lockSelections && setSelectedNumber(e.target.value)}
              disabled={lockSelections}
              placeholder="XX"
              min="1"
              max="42"
            />
            {selectedNumber && (
              <button
                type="button"
                className="minimal-wheel__number-clear"
                onClick={() => !lockSelections && setSelectedNumber(null)}
                aria-label="Clear number"
              >
                ✕
              </button>
            )}
          </div>

          {/* Spin Button */}
          <button
            className="minimal-wheel__spin-button"
            onClick={handleSpin}
            disabled={isSpinning}
          >
            SPIN!
          </button>

          {/* Result Display below spin button (misses go pastel red) */}
          {result && (
            <div
              className={
                result === 'NO MATCH'
                  ? 'minimal-wheel__result minimal-wheel__result--miss'
                  : 'minimal-wheel__result'
              }
            >
              <div className="minimal-wheel__result-text">{result}</div>
            </div>
          )}
          {resultDetail && (
            <div className="minimal-wheel__result-detail">{resultDetail}</div>
          )}

          {/* All winning numbers this game, in spin order, on one line */}
          {numberHistory.length > 0 && (
            <div className="minimal-wheel__history">
              {numberHistory.map((h, i) => (
                <span
                  key={i}
                  className="minimal-wheel__history-num"
                  style={{ color: h.color }}
                >
                  {h.number}
                </span>
              ))}
            </div>
          )}

          {/* Small ad banner under the spin button/results */}
          <AdBanner />
        </div>
      </div>

      {/* End-of-game summary */}
      {endGame && (
        <div className="minimal-wheel__endgame">
          <div className="minimal-wheel__endgame-card">
            <h2 className="minimal-wheel__endgame-title">
              {endGame.guest
                ? 'TRIAL COMPLETE!'
                : endGame.newHighScore ? 'NEW HIGH SCORE!' : 'GAME COMPLETE!'}
            </h2>
            <div className="minimal-wheel__endgame-score">
              {(endGame.finalScore ?? 0).toLocaleString()}
            </div>
            <div className="minimal-wheel__endgame-line">
              Best spin: +{(endGame.bestSpin ?? 0).toLocaleString()}
            </div>
            {endGame.guest ? (
              <>
                <div className="minimal-wheel__endgame-line">
                  Great run! Save your scores and keep spinning.
                </div>
                <button
                  className="minimal-wheel__endgame-button"
                  onClick={() => {
                    setEndGame(null);
                    openAuth('signup20');
                  }}
                >
                  SIGN UP — GET 20 FREE SPINS
                </button>
              </>
            ) : (
              <>
                <div className="minimal-wheel__endgame-line">
                  Personal best: {(endGame.highScore ?? 0).toLocaleString()}
                </div>
                <button className="minimal-wheel__endgame-button" onClick={handlePlayAgain}>
                  PLAY AGAIN
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MinimalWheel;
